import { cn } from '@/lib/utils';

interface SparklineProps {
  /** Daily counts, oldest → newest. Length determines x granularity. */
  data: number[];
  className?: string;
  /** Stroke color. */
  color?: string;
  /** Total height in px (line + bg). Defaults to 28. */
  height?: number;
  /** Total width in px. Defaults to 100% (responsive). */
  width?: number;
  /** Optional accessible label. */
  ariaLabel?: string;
}

/**
 * Lightweight SVG sparkline. No tooltip, no axes — just the line + a faint
 * area fill. Designed to live inside a dashboard tile.
 */
export function Sparkline({
  data,
  className,
  color = 'currentColor',
  height = 28,
  width,
  ariaLabel = 'Trend',
}: SparklineProps) {
  if (data.length === 0) {
    return (
      <div
        className={cn('flex items-center text-[9px] uppercase tracking-widest opacity-40', className)}
        style={{ height }}
        aria-hidden
      >
        —
      </div>
    );
  }

  const n = data.length;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(max - min, 1);
  const xStep = n > 1 ? 100 / (n - 1) : 0;
  const yPad = 4; // top/bottom padding so the line never hugs the edge

  const points = data.map((v, i) => {
    const x = i * xStep;
    const yNorm = (v - min) / range;
    const y = (1 - yNorm) * (100 - 2 * yPad) + yPad;
    return [x, y] as const;
  });

  const pathD = points
    .map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`))
    .join(' ');

  const areaD = `${pathD} L 100 100 L 0 100 Z`;

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      width={width ?? '100%'}
      height={height}
      className={cn('block', className)}
    >
      <path d={areaD} fill={color} opacity={0.12} />
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
